import '@/App.css';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import AuthCallback from './components/AuthCallback';
import { NotificationBanner } from './components/NotificationManager';
import UserTypeModal from './components/UserTypeModal';
import LocationPrompt from './components/LocationPrompt';
import PhoneRequiredModal from './components/PhoneRequiredModal';
import HomePage from './pages/HomePage';
import ExplorePage from './pages/ExplorePage';
import DealDetailPage from './pages/DealDetailPage';
import BusinessRegisterPage from './pages/BusinessRegisterPage';
import BusinessDashboardPage from './pages/BusinessDashboardPage';
import AdminPage from './pages/AdminPage';
import PricingPage from './pages/PricingPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import StudentDashboardPage from './pages/StudentDashboardPage';
import ReferralLandingPage from './pages/ReferralLandingPage';
import MarketingKitPage from './pages/MarketingKitPage';

function ProtectedRoute({ children, requiredRole }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F6F1E9] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#C8A97E] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (requiredRole && user.role !== requiredRole && !(requiredRole === 'business' && user.role === 'admin')) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRouter({ onUserLogin }) {
  const location = useLocation();
  const { user } = useAuth();

  // Trigger location prompt after first login
  useEffect(() => {
    if (user && !localStorage.getItem('perkly_user_loc') && !localStorage.getItem('perkly_loc_dismissed')) {
      onUserLogin?.();
    }
  }, [user, onUserLogin]);

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/explore" element={<ExplorePage />} />
      <Route path="/deals/:dealId" element={<DealDetailPage />} />
      <Route path="/business/register" element={<BusinessRegisterPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route
        path="/student/dashboard"
        element={
          <ProtectedRoute>
            <StudentDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/business/payment-success"
        element={
          <ProtectedRoute>
            <PaymentSuccessPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/business/dashboard"
        element={
          <ProtectedRoute>
            <BusinessDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminPage />
          </ProtectedRoute>
        }
      />
      <Route path="/r/:slug" element={<ReferralLandingPage />} />
      <Route path="/growth/kit" element={<MarketingKitPage />} />
      <Route path="/dashboard" element={<Navigate to="/student/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  const [locationDismissed, setLocationDismissed] = useState(
    () => localStorage.getItem('perkly_loc_dismissed') === 'true'
  );
  const [showLocation, setShowLocation] = useState(false);

  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter onUserLogin={() => setShowLocation(true)} />
          <NotificationBanner />
          <PhoneRequiredModal />
          <UserTypeModal />
          {showLocation && !locationDismissed && (
            <LocationPrompt
              onGranted={(pos) => { localStorage.setItem('perkly_user_loc', JSON.stringify(pos)); setShowLocation(false); }}
              onDismiss={() => { localStorage.setItem('perkly_loc_dismissed', 'true'); setLocationDismissed(true); setShowLocation(false); }}
            />
          )}
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
